import React, { useState, useEffect, useContext } from "react";
import "../../App.css";
import { FaSearch, FaPlus } from "react-icons/fa";
import AddCharities from "./AddCharities";
import { UserContext } from "../context/UserContext";

const CharitiesList = ({ onCharityClick }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddCharitiesOpen, setIsAddCharitiesOpen] = useState(false);
  const [charities, setCharities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const { isLoggedIn, emailId, phoneNo } = useContext(UserContext);

  // Move the styles constant inside the component
  const styles = `
    .charities-list::-webkit-scrollbar {
      width: 8px;
    }

    .charities-list::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }

    .charities-list::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 4px;
    }

    .charities-list::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
  `;

  // Move useEffect inside the component
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Screen size detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleCharitySelection = (charity) => {
    try {
      localStorage.setItem("selectedProjectId", charity.project_id);
      localStorage.setItem("selectionTimestamp", Date.now());
      // Dispatch custom event for immediate state update
      window.dispatchEvent(new CustomEvent('projectSelected', {
        detail: { projectId: charity.project_id }
      }));
      if (onCharityClick) {
        onCharityClick("charity");
      }
    } catch (error) {
      console.error("Error selecting charity:", error);
    }
  };

  // Fetch charities data
  useEffect(() => {
    const fetchCharities = async () => {
      try {
        const response = await fetch("/api/projectlist?type=charity");
        if (response.ok) {
          const data = await response.json();
          setCharities(data);
        } else {
          console.error("Failed to fetch charities");
        }
      } catch (error) {
        console.error("Error fetching charities:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCharities();
    const intervalId = setInterval(fetchCharities, 5000);
    return () => clearInterval(intervalId);
  }, []);

  const filteredCharities = charities.filter((charity) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      charity.project_title?.toLowerCase().includes(searchLower) ||
      charity.category?.toLowerCase().includes(searchLower) ||
      charity.description?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="charities-list-container" style={{ 
      padding: "10px",
      height: "100%",
      overflow: "hidden"
    }}>
      {/* Search bar and Add button - Fixed at top */}
      <div
        className="search-add-container"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "10px",
          backgroundColor: "#fff",
          zIndex: 1,
          padding: "10px 0"
        }}
      >
        {/* Search Bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            position: "relative",
            width: "300px",
            height: "40px",
            background: "linear-gradient(to right, #007bff, #00d4ff)",
            borderRadius: "50px",
            padding: "10px",
          }}
        >
          <FaSearch
            style={{
              fontSize: "18px",
              color: "#fff",
              marginRight: "10px",
            }}
          />
          <input
            type="text"
            placeholder="Search charities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              border: "none",
              outline: "none",
              backgroundColor: "transparent",
              color: "#fff",
              fontSize: "14px",
              caretColor: "#fff",
            }}
            className="search-input-placeholder"
          />
        </div>

        {/* Add Button - Only show when user is logged in */}
        {isLoggedIn && emailId && phoneNo && (
          <button
            onClick={() => setIsAddCharitiesOpen(true)}
            style={{
              background: "linear-gradient(to right, #007bff, #00d4ff)",
              color: "#fff",
              border: "none",
              padding: "10px",
              borderRadius: "50%",
              fontSize: "18px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              height: "40px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            <FaPlus />
          </button>
        )}
      </div>

      {/* Scrollable Charities List */}
      <div
        className="charities-list"
        style={{
          display: "grid",
          gap: "20px",
          maxHeight: "calc(100vh - 180px)",
          overflowY: "auto",
          padding: "10px 10px 100px 0", // Added 100px bottom padding
          marginRight: "5px"
        }}
      >
        {isLoading ? (
          <p>Loading charities...</p>
        ) : (
          filteredCharities.map((charity) => (
            <div
              key={charity.id}
              className="charity-card"
              onClick={() => handleCharitySelection(charity)}
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: "15px",
                padding: "10px",
                backgroundColor: "#fff",
                borderRadius: "10px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                alignItems: isMobile ? "center" : "flex-start",
                width: isMobile ? "100%" : "380px",
                cursor: "pointer",
              }}
            >
              {/* Thumbnail */}
              <div
                className="charity-thumbnail"
                style={{
                  width: isMobile ? "100%" : "150px",
                  height: isMobile ? "auto" : "100px",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                {charity.image_url?.match(/\.(jpeg|jpg|png|gif)$/i) ? (
                  <img
                    src={charity.image_url}
                    alt={charity.project_title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : charity.image_url?.match(/\.(mp4|mov|avi|mkv)$/i) ? (
                  <video
                    controls
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  >
                    <source src={charity.image_url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <p
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "#e0e0e0",
                      color: "#555",
                      fontSize: "12px",
                      textAlign: "center",
                    }}
                  >
                    No media available
                  </p>
                )}
              </div>

              {/* Charity Info */}
              <div
                className="charity-info"
                style={{
                  flex: "1",
                  marginTop: isMobile ? "10px" : "0",
                  textAlign: isMobile ? "center" : "left",
                }}
              >
                <h5
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginBottom: "4px",
                  }}
                >
                  {charity.project_title}
                </h5>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#555",
                    margin: "0 0 8px",
                    fontWeight: "bold",
                  }}
                >
                  #{charity.category}
                </p>
                <div style={{ marginTop: "5px" }}>
                  <div style={{
                    height: "10px",
                    background: "#e0e0e0",
                    borderRadius: "5px",
                    overflow: "hidden",
                  }}>
                    <div style={{
                      height: "100%",
                      width: `${Math.min((charity.total_raised / charity.target_amount) * 100, 100)}%`,
                      background: "linear-gradient(to right, #007bff, #00d4ff)",
                      borderRadius: "5px",
                      transition: "width 0.3s ease-in-out",
                    }}></div>
                  </div>
                  <p style={{ marginTop: "5px", fontSize: "12px", color: "#777" }}>
                    ${charity.total_raised || 0} raised of ${charity.target_amount}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* AddCharities Popup */}
      {isAddCharitiesOpen && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            zIndex: 1000,
          }}
        >
          <AddCharities onClose={() => setIsAddCharitiesOpen(false)} />
        </div>
      )}
    </div>
  );
};

export default CharitiesList;
